import { useCallback, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { Check, Copy } from 'lucide-react';

export interface CopyButtonProps {
  /** The exact text to place on the clipboard — never the formatted display string. */
  value: string;
  /** What was copied, for the tooltip and the screen-reader announcement. */
  label?: string;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Copies an identifier.
 *
 * Chassis and VIN numbers get retyped into other systems all day, so this sits next
 * to every stamped identifier. The async Clipboard API is unavailable on plain HTTP,
 * which a depot machine may well be on, so there is a selection-based fallback.
 */
export function CopyButton({ value, label = 'value', size = 'sm', className }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  const copy = useCallback(
    async (event: React.MouseEvent) => {
      // These live inside clickable table rows.
      event.stopPropagation();
      event.preventDefault();

      let ok = false;
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
          ok = true;
        }
      } catch {
        ok = false;
      }

      if (!ok) {
        // Fallback for insecure origins and older browsers.
        const scratch = document.createElement('textarea');
        scratch.value = value;
        scratch.setAttribute('readonly', '');
        scratch.style.position = 'fixed';
        scratch.style.opacity = '0';
        document.body.appendChild(scratch);
        scratch.select();
        try {
          ok = document.execCommand('copy');
        } catch {
          ok = false;
        }
        scratch.remove();
      }

      setCopied(ok);
      setFailed(!ok);
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        setCopied(false);
        setFailed(false);
      }, 1600);
    },
    [value],
  );

  const dimension = size === 'sm' ? 'h-5 w-5' : 'h-7 w-7';
  const icon = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';

  return (
    <button
      type="button"
      onClick={copy}
      title={failed ? `Could not copy the ${label}` : copied ? 'Copied' : `Copy the ${label}`}
      aria-label={`Copy the ${label}`}
      className={clsx(
        'tap-safe inline-flex shrink-0 items-center justify-center rounded-control border border-transparent',
        'text-faint transition-colors duration-150 hover:border-line hover:bg-surface-2 hover:text-ink-2',
        dimension,
        copied && 'text-success',
        failed && 'text-danger',
        className,
      )}
    >
      {copied ? <Check className={icon} aria-hidden /> : <Copy className={icon} aria-hidden />}
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? `${label} copied` : failed ? `${label} could not be copied` : ''}
      </span>
    </button>
  );
}
